import { MAX_UPLOAD_FILE_MB } from "../../utils/uploadLimits";

/**
 * Image Conversion tools. One prep screen and one Pillow convert endpoint
 * serve every pair; each tool only changes the accepted source and the target.
 */
export const CONVERSION_TOOL_IDS = {
  HEIC_TO_JPG: "heic_to_jpg",
  WEBP_TO_JPG: "webp_to_jpg",
  PNG_TO_JPG: "png_to_jpg",
  JPG_TO_PNG: "jpg_to_png",
  WEBP_TO_PNG: "webp_to_png",
};

const sizeHint = `Max ${MAX_UPLOAD_FILE_MB} MB`;

export const CONVERSION_TOOLS = {
  [CONVERSION_TOOL_IDS.HEIC_TO_JPG]: {
    key: CONVERSION_TOOL_IDS.HEIC_TO_JPG,
    title: "HEIC to JPG",
    source: "heic",
    target: "jpg",
    sourceLabel: "HEIC",
    targetLabel: "JPG",
    extensions: ["heic", "heif"],
    mimeTypes: [
      "image/heic",
      "image/heif",
      "image/heic-sequence",
      "image/heif-sequence",
    ],
    accept: ".heic,.heif,image/heic,image/heif",
    uploadLabel: "Upload HEIC",
    uploadHint: `Supported formats: HEIC, HEIF · ${sizeHint}`,
    subtitle: "Convert iPhone HEIC photos to JPG.",
    sortOrder: 1,
  },
  [CONVERSION_TOOL_IDS.WEBP_TO_JPG]: {
    key: CONVERSION_TOOL_IDS.WEBP_TO_JPG,
    title: "WebP to JPG",
    source: "webp",
    target: "jpg",
    sourceLabel: "WebP",
    targetLabel: "JPG",
    extensions: ["webp"],
    mimeTypes: ["image/webp"],
    accept: ".webp,image/webp",
    uploadLabel: "Upload WebP",
    uploadHint: `Supported format: WebP · ${sizeHint}`,
    subtitle: "Convert WebP images to JPG. Transparent areas become white.",
    sortOrder: 2,
  },
  [CONVERSION_TOOL_IDS.PNG_TO_JPG]: {
    key: CONVERSION_TOOL_IDS.PNG_TO_JPG,
    title: "PNG to JPG",
    source: "png",
    target: "jpg",
    sourceLabel: "PNG",
    targetLabel: "JPG",
    extensions: ["png"],
    mimeTypes: ["image/png"],
    accept: ".png,image/png",
    uploadLabel: "Upload PNG",
    uploadHint: `Supported format: PNG · ${sizeHint}`,
    subtitle: "Convert PNG images to JPG. Transparent areas become white.",
    sortOrder: 3,
  },
  [CONVERSION_TOOL_IDS.JPG_TO_PNG]: {
    key: CONVERSION_TOOL_IDS.JPG_TO_PNG,
    title: "JPG to PNG",
    source: "jpg",
    target: "png",
    sourceLabel: "JPG",
    targetLabel: "PNG",
    extensions: ["jpg", "jpeg"],
    mimeTypes: ["image/jpeg", "image/jpg"],
    accept: ".jpg,.jpeg,image/jpeg",
    uploadLabel: "Upload JPG",
    uploadHint: `Supported formats: JPG, JPEG · ${sizeHint}`,
    subtitle: "Convert JPG images to PNG without changing the picture.",
    sortOrder: 4,
  },
  [CONVERSION_TOOL_IDS.WEBP_TO_PNG]: {
    key: CONVERSION_TOOL_IDS.WEBP_TO_PNG,
    title: "WebP to PNG",
    source: "webp",
    target: "png",
    sourceLabel: "WebP",
    targetLabel: "PNG",
    extensions: ["webp"],
    mimeTypes: ["image/webp"],
    accept: ".webp,image/webp",
    uploadLabel: "Upload WebP",
    uploadHint: `Supported format: WebP · ${sizeHint}`,
    subtitle: "Convert WebP images to PNG and keep transparency.",
    sortOrder: 5,
  },
};

export const CONVERSION_TOOL_LIST = Object.values(CONVERSION_TOOLS).sort(
  (a, b) => a.sortOrder - b.sortOrder
);

export const CONVERSION_TOOL_SUBTITLES = Object.fromEntries(
  CONVERSION_TOOL_LIST.map((tool) => [tool.key, tool.subtitle])
);

export const IMAGE_CONVERSION_CATEGORY_SLUG = "image_conversion";

export function getConversionTool(toolKey) {
  return CONVERSION_TOOLS[toolKey] || null;
}

export function fileMatchesConversion(file, toolKey) {
  const spec = getConversionTool(toolKey);
  if (!spec || !file) return false;
  const name = String(file.name || "").toLowerCase();
  const ext = name.includes(".") ? name.slice(name.lastIndexOf(".") + 1) : "";
  if (spec.extensions.includes(ext)) return true;
  const type = String(file.type || "").toLowerCase();
  return spec.mimeTypes.includes(type);
}

export function conversionRejectMessage(toolKey) {
  const spec = getConversionTool(toolKey);
  const label = spec?.sourceLabel || "matching";
  return `Upload a ${label} image for this tool.`;
}

export function conversionOutputName(sourceName, targetExt) {
  const raw = String(sourceName || "image").replace(/\.[^.]+$/, "");
  const cleaned = raw.replace(/[^\w\s\-]/g, "").replace(/\s+/g, " ").trim();
  const base = (cleaned || "image").slice(0, 180);
  return `${base}.${targetExt || "jpg"}`;
}

/** Dashboard section used when the catalog API has not returned this category yet. */
export function imageConversionFallbackSection() {
  return {
    slug: IMAGE_CONVERSION_CATEGORY_SLUG,
    name: "Image Conversion",
    description: null,
    display_order: 6,
    tools: CONVERSION_TOOL_LIST.map((tool) => ({
      tool_key: tool.key,
      card_label: tool.title,
      subtitle: tool.subtitle,
      card_style: "text",
      card_image_url: null,
      sort_order: tool.sortOrder,
      metadata: {
        action: "open_editor_panel",
        editor_panel: tool.key,
        clickable: true,
      },
    })),
  };
}

export function conversionHowToUse(toolKey) {
  const spec = getConversionTool(toolKey);
  if (!spec) return null;
  const jpgNote =
    spec.target === "jpg"
      ? "Transparent areas are filled with white, because JPG has no transparency."
      : "PNG keeps transparency when the source has it.";
  return {
    aka: spec.title,
    summary: spec.subtitle,
    steps: [
      `Upload a ${spec.sourceLabel} image`,
      "Check it on the canvas",
      `Download to convert it to ${spec.targetLabel}`,
    ],
    tip: jpgNote,
  };
}
